import { PRIMARY_COLOR, SECONDARY_COLOR } from '../theme/palette';
// Service for managing store branding settings
class BrandingService {
  constructor() {
    this.subscribers = [];
    // Last logo written to the tab icon, so repeated notifications don't
    // rebuild the <link> element on every settings update.
    this.appliedFavicon = null;
    this.settings = this.getDefaultSettings();
    this.loadFromStorage();
    this.loadFromBackend(); // Load from backend on initialization
  }

  getDefaultSettings() {
    return {
      // Indicates whether we've attempted to load settings from the backend
      backendLoaded: false,
      // True when the backend could not be reached at all — the storefront
      // shows a network message instead of guessing at a design.
      backendUnreachable: false,
      // Site-wide defaults; a boutique's own colours (set in Admin > Boutiques) override these.
      primaryColor: PRIMARY_COLOR,
      secondaryColor: SECONDARY_COLOR,
      storeLogo: '/logo.jpg',
      heroSectionImages: {
        mainImage: 'https://images.unsplash.com/photo-1621072156002-e2fccdc0b176?q=80&w=1374&auto=format&fit=crop',
        leftFloatingImage: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?q=80&w=2080&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=60',
        rightFloatingImage: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8ZmFzaGlvbiUyMGRldGFpbHxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=500&q=60',
        bottomFloatingImage: 'https://images.unsplash.com/photo-1560769629-975ec94e6a86?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGZhc2hpb24lMjBhY2Nlc3Nvcnl8ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=500&q=60'
      },
      loginPageImages: [
        {
          id: 1,
          title: "Accessoires Exclusifs",
          description: "Découvrez notre nouvelle collection d'accessoires premium pour compléter votre style.",
          image: "https://images.unsplash.com/photo-1518002171953-a080ee817e1f?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
          accent: "#FFFFFF"
        },
        {
          id: 2,
          title: "Style Premium",
          description: "Explorez notre sélection de vêtements haut de gamme conçus pour les connaisseurs.",
          image: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?q=80&w=1800&auto=format&fit=crop",
          accent: "#10B981"
        },
        {
          id: 3,
          title: "Élégance Moderne",
          description: "Une expérience shopping exceptionnelle avec les dernières tendances mode.",
          image: "https://images.unsplash.com/photo-1508427953056-b00b8d78ebf5?q=80&w=1800&auto=format&fit=crop",
          accent: "#8B5CF6"
        }
      ]
    };
  }

  async loadFromBackend() {
    try {
      // Import the settings viewmodel to get data from backend
      const { loadSettings } = await import('../pages/admin/settings/viewmodel/settings-viewmodel');
      const { resolveCurrentStore } = await import('./storeService');
      const [backendSettings, store] = await Promise.all([loadSettings(), resolveCurrentStore()]);
      
      if (backendSettings) {
        // Map backend settings to branding format
        const mappedSettings = this.mapBackendToBranding(backendSettings);
        this.settings = { ...this.settings, ...mappedSettings };
      }
      // The store that owns this domain overrides the global theme
      if (store) {
        this.settings = { ...this.settings, ...this.mapStoreToBranding(store) };
      }
      // Nothing came back at all — treat it as a network failure so the
      // storefront can say so rather than render a half-configured store.
      this.settings = {
        ...this.settings,
        backendUnreachable: !backendSettings && !store,
      };

      if (backendSettings || store) {
        this.applyCSSVariables();
        this.applyFavicon();
        this.notifySubscribers();
      }
    } catch (error) {
      console.warn('Failed to load settings from backend:', error);
      this.settings = { ...this.settings, backendUnreachable: true };
    }
    finally {
      // Mark that we've completed the backend load attempt (success or failure)
      try {
        this.settings = { ...this.settings, backendLoaded: true };
        this.notifySubscribers();
      } catch (e) {
        // ignore
      }
    }
  }

  mapStoreToBranding(store) {
    const mapped = { storeId: store.id };
    if (store.nom) mapped.shopName = store.nom;
    // The boutique's own colours (Admin > Boutiques) override the site default.
    if (store.couleurPrincipale) mapped.primaryColor = store.couleurPrincipale;
    if (store.couleurSecondaire) mapped.secondaryColor = store.couleurSecondaire;
    // Logo has its own field; stores saved before it still carry it in the gallery.
    if (store.logoUrl) mapped.storeLogo = store.logoUrl;
    else if (store.imageUrl && store.imageUrl.length > 0) mapped.storeLogo = store.imageUrl[0];
    if (store.bannerText) mapped.bannerText = store.bannerText;
    return mapped;
  }

  mapBackendToBranding(backendSettings) {
    const mappedSettings = {};
    
    // Logo and shop title; colours come from the active boutique (mapStoreToBranding), not here.
    if (backendSettings.storeLogo) mappedSettings.storeLogo = backendSettings.storeLogo;
    if (backendSettings.title) mappedSettings.shopName = backendSettings.title;
    
    // Map hero section images
    if (backendSettings.mainImage || backendSettings.leftFloatingImage || backendSettings.rightFloatingImage || backendSettings.bottomFloatingImage) {
      mappedSettings.heroSectionImages = {
        mainImage: backendSettings.mainImage || this.settings.heroSectionImages.mainImage,
        leftFloatingImage: backendSettings.leftFloatingImage || this.settings.heroSectionImages.leftFloatingImage,
        rightFloatingImage: backendSettings.rightFloatingImage || this.settings.heroSectionImages.rightFloatingImage,
        bottomFloatingImage: backendSettings.bottomFloatingImage || this.settings.heroSectionImages.bottomFloatingImage
      };
      // Mark that hero images/content came from backend
      mappedSettings.heroFromBackend = true;
    }

    // Map hero section textual content if provided
    if (backendSettings.heroTitle || backendSettings.heroSubtitle || backendSettings.heroDescription || backendSettings.heroTag || backendSettings.heroCollectionTitle) {
      mappedSettings.heroSectionContent = {
        titleLine1: backendSettings.heroTitle || null,
        titleLine2: backendSettings.heroSubtitle || null,
        description: backendSettings.heroDescription || null,
        tag: backendSettings.heroTag || null,
        collectionTitle: backendSettings.heroCollectionTitle || null
      };
      mappedSettings.heroFromBackend = true;
    }
    
    // Map login page images
    if (backendSettings.loginImage1 || backendSettings.loginImage2 || backendSettings.loginImage3) {
      mappedSettings.loginPageImages = [
        {
          id: 1,
          title: backendSettings.title1 || "Accessoires Exclusifs",
          description: backendSettings.description1 || "Découvrez notre nouvelle collection d'accessoires premium pour compléter votre style.",
          image: backendSettings.loginImage1 || this.settings.loginPageImages[0].image,
          accent: backendSettings.accent1 || this.settings.secondaryColor || "#fc4c00"
        },
        {
          id: 2,
          title: backendSettings.title2 || "Style Premium",
          description: backendSettings.description2 || "Explorez notre sélection de vêtements haut de gamme conçus pour les connaisseurs.",
          image: backendSettings.loginImage2 || this.settings.loginPageImages[1].image,
          accent: backendSettings.accent2 || this.settings.primaryColor || "#4983a9"
        },
        {
          id: 3,
          title: backendSettings.title3 || "Élégance Moderne",
          description: backendSettings.description3 || "Une expérience shopping exceptionnelle avec les dernières tendances mode.",
          image: backendSettings.loginImage3 || this.settings.loginPageImages[2].image,
          accent: backendSettings.accent3 || this.settings.secondaryColor || "#fc4c00"
        }
      ];
    }
    
    return mappedSettings;
  }

  // Sync with backend when settings change
  async syncWithBackend() {
    try {
      await this.loadFromBackend();
    } catch (error) {
      console.warn('Failed to sync with backend:', error);
    }
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem('store_branding');
      if (stored) {
        // storeTemplate selection was removed; colours are kept (boutique-specific).
        const { storeTemplate, ...rest } = JSON.parse(stored);
        this.settings = {
          ...this.settings,
          ...rest,
        };
      }
    } catch (error) {
      console.error('Error loading branding settings from storage:', error);
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem('store_branding', JSON.stringify(this.settings));
    } catch (error) {
      console.error('Error saving branding settings to storage:', error);
    }
  }

  updateSettings(newSettings) {
    // storeTemplate selection was removed; colour changes are allowed through.
    const { storeTemplate, ...allowed } = newSettings || {};
    this.settings = {
      ...this.settings,
      ...allowed,
    };
    this.saveToStorage();
    this.applyCSSVariables();
    this.applyFavicon();
    this.notifySubscribers();
  }

  // Refresh settings from backend (to be called after settings are saved)
  async refreshFromBackend() {
    await this.loadFromBackend();
  }

  getSettings() {
    return this.settings;
  }

  getPrimaryColor() {
    return this.settings.primaryColor;
  }

  getSecondaryColor() {
    return this.settings.secondaryColor;
  }

  getStoreLogo() {
    return this.settings.storeLogo;
  }

  getHeroImages() {
    return this.settings.heroSectionImages;
  }

  getLoginImages() {
    return this.settings.loginPageImages;
  }

  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.settings));
  }

  // Apply CSS custom properties for dynamic theming
  applyCSSVariables() {
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.style.setProperty('--primary-color', this.settings.primaryColor);
      root.style.setProperty('--secondary-color', this.settings.secondaryColor);
      
      // Convert hex to RGB for alpha transparency support
      const primaryRgb = this.hexToRgb(this.settings.primaryColor);
      const secondaryRgb = this.hexToRgb(this.settings.secondaryColor);
      
      if (primaryRgb) {
        root.style.setProperty('--primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      }
      if (secondaryRgb) {
        root.style.setProperty('--secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
      }
    }
  }

  // Point the browser tab icon at the boutique's logo. Falls back to whatever
  // index.html declared when the store has no logo of its own.
  applyFavicon() {
    if (typeof document === 'undefined') return;

    const logo = this.settings.storeLogo;
    if (!logo || logo === this.appliedFavicon) return;

    // `rel~='icon'` matches both `icon` and `shortcut icon`, but not
    // `apple-touch-icon`, which keeps its own asset.
    const existing = document.querySelectorAll("link[rel~='icon']");
    existing.forEach(node => node.parentNode?.removeChild(node));

    // Chrome re-reads the icon when the link element is replaced rather than
    // mutated in place, so append a fresh one. No `type` is declared: the
    // boutique's file may be any image format and the browser can sniff it.
    const link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    link.setAttribute('href', logo);
    document.head.appendChild(link);

    this.appliedFavicon = logo;
  }

  // Helper function to convert hex to RGB
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  // Initialize CSS variables and the tab icon on app startup
  init() {
    this.applyCSSVariables();
    this.applyFavicon();
  }
}

// Create a singleton instance
const brandingService = new BrandingService();

export default brandingService;
